import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api_url } from "../config.js";
import { listAllDesktops, listMyDesktops } from "../slices/desktopSlice";

// initialDesktopCheckDone flag to ensure that the empty-desktop redirect to CreateDesktopPage only happens exactly once after login
let initialDesktopCheckDone = false;

const GlobalSSEListener = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sseRef = useRef(null);
  const lastEventId = useRef(null);
  const hasFetchedInitial = useRef(false);

  // safe optional chaining so it won't crash if state.user is temporarily null
  const userInfo = useSelector((state) => state.user?.userInfo);

  // fetching token directly from Redux, falling back to localStorage for the initial render
  const token =
    useSelector((state) => state.user?.token) ||
    JSON.parse(localStorage.getItem("keycloak") || "{}")?.token;

  useEffect(() => {
    // connect only when we have both the token and the user's ID
    if (!token || !userInfo?.id) return;

    let sseUrl = `${api_url}/system/events?access_token=${token}`;

    if (lastEventId.current) {
      sseUrl += `&lastEventId=${lastEventId.current}`;
    }

    if (sseRef.current) {
      sseRef.current.close();
    }

    const eventSource = new EventSource(sseUrl);
    sseRef.current = eventSource;

    eventSource.addEventListener("Connected", (e) => {
      if (e.lastEventId) lastEventId.current = e.lastEventId;
      const data = JSON.parse(e.data);

      if (
        !hasFetchedInitial.current ||
        data.MissedEvents ||
        data.missedEvents
      ) {
        const isFirstFetch = !hasFetchedInitial.current;
        hasFetchedInitial.current = true;

        dispatch(userInfo?.isAdmin ? listAllDesktops() : listMyDesktops())
          .unwrap()
          .then((payload) => {
            if (isFirstFetch && !initialDesktopCheckDone) {
              initialDesktopCheckDone = true;
              const fetchedDesktops = Array.isArray(payload)
                ? payload
                : payload?.desktops || [];
              if (fetchedDesktops.length === 0) {
                navigate("/create-desktop", { replace: true });
              }
            }
          })
          .catch((err) => {
            if (isFirstFetch) hasFetchedInitial.current = false;
            console.error("Error fetching desktops:", err);
          });
      }
    });

    eventSource.addEventListener("DesktopCreated", (e) => {
      if (e.lastEventId) lastEventId.current = e.lastEventId;
      const data = JSON.parse(e.data);
      dispatch({ type: "desktop/create/fulfilled", payload: data.desktop });
      toast.success(`Desktop ${data.desktop.name} created!`);
    });

    eventSource.addEventListener("DesktopStateChanged", (e) => {
      if (e.lastEventId) lastEventId.current = e.lastEventId;
      const data = JSON.parse(e.data);
      dispatch({ type: "desktop/update/fulfilled", payload: data.desktop });
    });

    eventSource.addEventListener("DesktopPatched", (e) => {
      if (e.lastEventId) lastEventId.current = e.lastEventId;
      const data = JSON.parse(e.data);
      dispatch({ type: "desktop/update/fulfilled", payload: data.desktop });
    });

    eventSource.addEventListener("DesktopDeleted", (e) => {
      if (e.lastEventId) lastEventId.current = e.lastEventId;
      const data = JSON.parse(e.data);
      dispatch({ type: "desktop/delete/fulfilled", payload: data.vmId });
    });

    eventSource.addEventListener("ErrorMessage", (e) => {
      if (e.lastEventId) lastEventId.current = e.lastEventId;
      const data = JSON.parse(e.data);
      toast.error(data.problem?.detail || "Server error");
    });

    eventSource.onerror = (err) => console.error("SSE Error:", err);

    eventSource.addEventListener("TokenExpired", (e) => {
      if (e.lastEventId) lastEventId.current = e.lastEventId;
      eventSource.close();
    });

    return () => {
      if (sseRef.current) sseRef.current.close();
    };
  }, [token, userInfo?.id, userInfo?.isAdmin, dispatch, navigate]);

  return null;
};

export default GlobalSSEListener;
